(() => {
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  const storage = {
    get(key) {
      try { return window.sessionStorage.getItem(key); } catch (e) { return null; }
    },
    set(key, value) {
      try { window.sessionStorage.setItem(key, value); } catch (e) {}
    }
  };

  if (!customElements.get('rtd-dismissible')) {
    customElements.define('rtd-dismissible', class extends HTMLElement {
      connectedCallback() {
        const key = this.dataset.key;
        if (!this.hasAttribute('data-dismissible')) return;
        if (!window.Shopify || !window.Shopify.designMode) {
          if (key && storage.get(key) === '1') {
            this.hidden = true;
            return;
          }
        }
        const button = this.querySelector('[data-dismiss]');
        if (button) {
          button.addEventListener('click', () => {
            this.hidden = true;
            if (key) storage.set(key, '1');
            window.dispatchEvent(new Event('resize'));
          });
        }
      }
    });
  }

  if (!customElements.get('rtd-scroller')) {
    customElements.define('rtd-scroller', class extends HTMLElement {
      connectedCallback() {
        this.track = this.querySelector('[data-track]');
        if (!this.track) return;
        this.prev = this.querySelector('[data-prev]');
        this.next = this.querySelector('[data-next]');
        this.onScroll = this.update.bind(this);
        this.track.addEventListener('scroll', this.onScroll, { passive: true });
        if (this.prev) this.prev.addEventListener('click', () => this.step(-1));
        if (this.next) this.next.addEventListener('click', () => this.step(1));
        this.resizeObserver = new ResizeObserver(() => this.update());
        this.resizeObserver.observe(this.track);
        this.update();
      }
      disconnectedCallback() {
        if (this.track) this.track.removeEventListener('scroll', this.onScroll);
        if (this.resizeObserver) this.resizeObserver.disconnect();
      }
      step(direction) {
        const item = this.track.querySelector('[data-item]');
        const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
        const width = item ? item.getBoundingClientRect().width + gap : this.track.clientWidth * 0.8;
        const visible = Math.max(1, Math.floor((this.track.clientWidth + gap) / width));
        this.track.scrollBy({ left: direction * width * visible, behavior: reduceMotion.matches ? 'auto' : 'smooth' });
      }
      update() {
        const max = this.track.scrollWidth - this.track.clientWidth - 2;
        const left = Math.abs(this.track.scrollLeft);
        if (this.prev) this.prev.disabled = left <= 2;
        if (this.next) this.next.disabled = left >= max;
        this.toggleAttribute('data-scrollable', max > 2);
      }
    });
  }

  if (!customElements.get('rtd-slider')) {
    customElements.define('rtd-slider', class extends HTMLElement {
      connectedCallback() {
        this.track = this.querySelector('[data-track]');
        this.slides = Array.from(this.querySelectorAll('[data-slide]'));
        this.dots = Array.from(this.querySelectorAll('[data-dot]'));
        this.index = 0;
        if (!this.track || this.slides.length < 2) return;
        this.delay = parseInt(this.dataset.autoplay || '0', 10) * 1000;
        this.style.setProperty('--rtd-autoplay', `${this.delay || 6000}ms`);

        const prev = this.querySelector('[data-prev]');
        const next = this.querySelector('[data-next]');
        if (prev) prev.addEventListener('click', () => this.go(this.index - 1, true));
        if (next) next.addEventListener('click', () => this.go(this.index + 1, true));
        this.dots.forEach((dot, i) => dot.addEventListener('click', () => this.go(i, true)));

        this.observer = new IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting && entry.intersectionRatio > 0.6) {
              this.setActive(this.slides.indexOf(entry.target));
            }
          });
        }, { root: this.track, threshold: [0.6] });
        this.slides.forEach((slide) => this.observer.observe(slide));

        this.addEventListener('mouseenter', () => this.pause());
        this.addEventListener('mouseleave', () => this.play());
        this.addEventListener('focusin', () => this.pause());
        this.addEventListener('focusout', () => this.play());
        this.track.addEventListener('pointerdown', () => this.stop());

        this.addEventListener('shopify:block:select', (event) => {
          const i = this.slides.findIndex((s) => s.contains(event.target) || s === event.target);
          if (i > -1) { this.stop(); this.go(i, false); }
        });

        this.setActive(0);
        this.play();
      }
      disconnectedCallback() {
        this.pause();
        if (this.observer) this.observer.disconnect();
      }
      go(i, userInitiated) {
        const count = this.slides.length;
        const target = (i + count) % count;
        const slide = this.slides[target];
        this.track.scrollTo({ left: slide.offsetLeft, behavior: reduceMotion.matches ? 'auto' : 'smooth' });
        this.setActive(target);
        if (userInitiated) this.restart();
      }
      setActive(i) {
        if (i < 0) return;
        this.index = i;
        this.slides.forEach((slide, n) => {
          slide.toggleAttribute('data-active', n === i);
          slide.setAttribute('aria-hidden', n === i ? 'false' : 'true');
          slide.querySelectorAll('a, button').forEach((el) => {
            if (n === i) el.removeAttribute('tabindex'); else el.setAttribute('tabindex', '-1');
          });
        });
        this.dots.forEach((dot, n) => {
          dot.setAttribute('aria-current', n === i ? 'true' : 'false');
          dot.classList.remove('is-running');
          if (n === i && this.timer) {
            void dot.offsetWidth;
            dot.classList.add('is-running');
          }
        });
      }
      play() {
        if (!this.delay || reduceMotion.matches || this.stopped || (window.Shopify && window.Shopify.designMode)) return;
        this.pause();
        this.timer = window.setInterval(() => this.go(this.index + 1, false), this.delay);
        const dot = this.dots[this.index];
        if (dot) { dot.classList.remove('is-running'); void dot.offsetWidth; dot.classList.add('is-running'); }
      }
      pause() {
        if (this.timer) window.clearInterval(this.timer);
        this.timer = null;
        this.dots.forEach((dot) => dot.classList.remove('is-running'));
      }
      stop() {
        this.stopped = true;
        this.pause();
      }
      restart() {
        if (this.stopped) return;
        this.play();
      }
    });
  }

  if (!customElements.get('rtd-tabs')) {
    customElements.define('rtd-tabs', class extends HTMLElement {
      connectedCallback() {
        this.tabs = Array.from(this.querySelectorAll('[role="tab"]'));
        this.panels = Array.from(this.querySelectorAll('[role="tabpanel"]'));
        this.indicator = this.querySelector('[data-indicator]');
        this.tabs.forEach((tab, i) => {
          tab.addEventListener('click', () => this.select(i, false));
          tab.addEventListener('keydown', (event) => {
            let n = null;
            if (event.key === 'ArrowRight') n = (i + 1) % this.tabs.length;
            if (event.key === 'ArrowLeft') n = (i - 1 + this.tabs.length) % this.tabs.length;
            if (event.key === 'Home') n = 0;
            if (event.key === 'End') n = this.tabs.length - 1;
            if (n !== null) { event.preventDefault(); this.select(n, true); }
          });
        });
        this.addEventListener('shopify:block:select', (event) => {
          const i = this.panels.findIndex((p) => p.dataset.blockId && event.detail && p.dataset.blockId === event.detail.blockId);
          if (i > -1) this.select(i, false);
        });
        this.resizeObserver = new ResizeObserver(() => this.moveIndicator());
        this.resizeObserver.observe(this);
        this.moveIndicator();
      }
      disconnectedCallback() {
        if (this.resizeObserver) this.resizeObserver.disconnect();
      }
      select(i, focus) {
        this.tabs.forEach((tab, n) => {
          const active = n === i;
          tab.setAttribute('aria-selected', active ? 'true' : 'false');
          tab.tabIndex = active ? 0 : -1;
          if (active && focus) tab.focus();
        });
        this.panels.forEach((panel, n) => { panel.hidden = n !== i; });
        this.moveIndicator();
      }
      moveIndicator() {
        if (!this.indicator) return;
        const active = this.tabs.find((t) => t.getAttribute('aria-selected') === 'true');
        if (!active) return;
        const list = active.parentElement;
        this.indicator.style.width = `${active.offsetWidth}px`;
        this.indicator.style.transform = `translateX(${active.offsetLeft - list.scrollLeft}px)`;
      }
    });
  }

  if (!customElements.get('rtd-faq')) {
    customElements.define('rtd-faq', class extends HTMLElement {
      connectedCallback() {
        this.items = Array.from(this.querySelectorAll('[data-faq-item]'));
        this.groups = Array.from(this.querySelectorAll('[data-faq-group]'));
        this.filters = Array.from(this.querySelectorAll('[data-faq-filter]'));
        this.search = this.querySelector('[data-faq-search]');
        this.empty = this.querySelector('[data-faq-empty]');
        this.count = this.querySelector('[data-faq-count]');
        this.category = 'all';

        this.filters.forEach((btn) => btn.addEventListener('click', () => {
          this.category = btn.dataset.faqFilter;
          this.filters.forEach((b) => b.setAttribute('aria-pressed', b === btn ? 'true' : 'false'));
          this.apply();
        }));
        if (this.search) {
          this.search.addEventListener('input', () => this.apply());
          const form = this.search.closest('form');
          if (form) form.addEventListener('submit', (e) => e.preventDefault());
        }
        this.items.forEach((item) => {
          item.addEventListener('toggle', () => {
            if (item.open && this.hasAttribute('data-single-open')) {
              this.items.forEach((other) => { if (other !== item) other.open = false; });
            }
          });
        });
        this.openFromHash();
        window.addEventListener('hashchange', () => this.openFromHash());
      }
      normalize(value) {
        return (value || '').toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/ß/g, 'ss');
      }
      apply() {
        const q = this.normalize(this.search ? this.search.value.trim() : '');
        let visible = 0;
        this.items.forEach((item) => {
          const inCategory = this.category === 'all' || item.dataset.category === this.category;
          const matches = !q || this.normalize(item.textContent).includes(q);
          const show = inCategory && matches;
          item.hidden = !show;
          if (show) visible += 1;
          if (q && show && q.length > 2) item.open = true;
        });
        this.groups.forEach((heading) => {
          const id = heading.dataset.faqGroup;
          heading.hidden = !this.items.some((item) => item.dataset.category === id && !item.hidden);
        });
        if (this.empty) this.empty.hidden = visible > 0;
        if (this.count) this.count.textContent = String(visible);
      }
      openFromHash() {
        if (!window.location.hash) return;
        let target = null;
        try { target = this.querySelector(window.location.hash); } catch (e) { target = null; }
        if (target && target.matches('[data-faq-item]')) {
          target.open = true;
          target.scrollIntoView({ block: 'center', behavior: reduceMotion.matches ? 'auto' : 'smooth' });
        }
      }
    });
  }

  const positionMega = (item) => {
    const panel = item.querySelector(':scope > .menu-list__submenu');
    if (!panel || item.slot === 'overflow') return;
    const trigger = item.querySelector('.menu-list__trigger') || item;
    const margin = 16;
    const viewport = document.documentElement.clientWidth;
    panel.style.left = '0px';
    const base = panel.getBoundingClientRect().left;
    const width = panel.offsetWidth;
    const rect = trigger.getBoundingClientRect();
    const center = rect.left + rect.width / 2;
    const left = Math.max(margin, Math.min(center - width / 2, viewport - margin - width));
    panel.style.left = (left - base) + 'px';
    const mega = panel.querySelector('.rtd-mega');
    if (mega) mega.style.setProperty('--rtd-mega-arrow', Math.max(28, Math.min(width - 28, center - left)) + 'px');
  };

  const megaItemFrom = (target) => {
    if (!(target instanceof Element)) return null;
    const item = target.closest('.menu-list__list-item');
    return item && item.closest('.header-menu--rtd') ? item : null;
  };

  const positionAllMega = () => {
    document.querySelectorAll('.header-menu--rtd .menu-list__list-item').forEach(positionMega);
  };

  document.addEventListener('pointerover', (e) => {
    const item = megaItemFrom(e.target);
    if (item && !item.querySelector(':scope > .menu-list__submenu')?.contains(e.target)) positionMega(item);
  });
  document.addEventListener('focusin', (e) => {
    const item = megaItemFrom(e.target);
    if (item) positionMega(item);
  });
  window.addEventListener('resize', positionAllMega, { passive: true });
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', positionAllMega);
  } else {
    positionAllMega();
  }
  document.addEventListener('shopify:section:load', positionAllMega);

})();
